import React, { useEffect } from "react"
import { Progress, Typography, Box, Link, Button, Stack, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, FormattedList, useIntl, defineMessage } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { TypeaheadDoula } from "#/pages/provider-finder/recommend-doula/RecommendDoulaForm"
import { Provider } from "#/pages/provider-finder/types/Providers"
import { useDoulasByIds } from "#/pages/provider-finder/hooks/useProvidersByProviderUuids"
import { useGetLocationDetails } from "#/pages/provider-finder/hooks/useGetLocationDetails"
import { PROVIDER_TYPE_SEARCHED } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"
import {
  getProviderFinderSearchURL,
  RedirectToProviderFinderURL
} from "#/pages/provider-finder/utils/providerFinderSearchUrl"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"

enum EligibilityStatus {
  NOT_ELIGIBLE = "not eligible",
  NOT_APPLICABLE = "n/a",
  PENDING = "pending",
  ELIGIBLE = "eligible",
  NOT_PRACTICING = "not practicing"
}

const doulaServiceLabels: Record<string, ReturnType<typeof defineMessage>> = {
  birth: defineMessage({ defaultMessage: "birth" }),
  postpartum: defineMessage({ defaultMessage: "postpartum" }),
  "pregnancy loss": defineMessage({ defaultMessage: "pregnancy loss" })
}

function CenteredProgress(): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        insetBlockStart: 0,
        insetInlineStart: 0,
        insetInlineEnd: 0,
        insetBlockEnd: 0
      }}
    >
      <Progress />
    </Box>
  )
}

export function RecommendDoulaStatus({ doula, onReset }: { doula: TypeaheadDoula; onReset?: () => void }): JSX.Element {
  const doulaStatus = doula?.eligibilityStatus?.toLowerCase()

  switch (doulaStatus) {
    case EligibilityStatus.NOT_PRACTICING:
    case EligibilityStatus.NOT_ELIGIBLE:
      return <NotEligible onReset={onReset} />
    case EligibilityStatus.NOT_APPLICABLE:
    case EligibilityStatus.PENDING:
      return <Pending />
    case EligibilityStatus.ELIGIBLE:
      return <EligibleDoulaRedirect doula={doula} />
    default:
      return <ThankYou />
  }
}

function NotEligible({ onReset }: { onReset?: () => void }): JSX.Element {
  const { mutate: addMemberAction } = useMemberActionTracking()

  useEffect(() => {
    addMemberAction({
      memberActionTaken: MemberActions.DOULA_RECOMMENDATION_NOT_APPROVED
    })
  }, [addMemberAction])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minBlockSize: "32rem" }}>
      <Box>
        <Box sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xs })}>
          <Typography variant="h3">
            <FormattedMessage defaultMessage="Not approved" />
          </Typography>
        </Box>
        <Box sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
          <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
            <FormattedMessage defaultMessage="We’ve reviewed this doula’s credentials previously. Unfortunately, they do not meet our requirements at this time. Please recommend a different doula or consider another doula in our network." />
          </Typography>
        </Box>
        <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
          <FormattedMessage
            defaultMessage="Questions? <link>Contact us</link>."
            values={{
              link: (linkContent) => (
                <Link component={RouterLink} to="/talk-to-carrot/sendmessage/helpfindingdoula">
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={() => {
            addMemberAction({
              memberActionTaken: MemberActions.PROVIDER_FINDER_RECOMMEND_A_DOULA_OPENED,
              metaData: {
                location: "Not approved modal"
              }
            })
            onReset()
          }}
        >
          <FormattedMessage defaultMessage="Recommend another doula" />
        </Button>
      </Box>
    </Box>
  )
}

function Pending(): JSX.Element {
  const theme = useTheme()
  const { mutate: addMemberAction } = useMemberActionTracking()

  useEffect(() => {
    addMemberAction({
      memberActionTaken: MemberActions.DOULA_RECOMMENDATION_PENDING
    })
  }, [addMemberAction])

  return (
    <Stack spacing={theme.tokens.spacing.xs}>
      <Typography variant="h3">
        <FormattedMessage defaultMessage="Review pending" />
      </Typography>
      <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
        <FormattedMessage defaultMessage="We’re currently reviewing this doula’s credentials. Please check back on this doula’s status at a later date. " />
      </Typography>
    </Stack>
  )
}

function ThankYou(): JSX.Element {
  const theme = useTheme()

  return (
    <Stack spacing={theme.tokens.spacing.xs}>
      <Typography variant="h3">
        <FormattedMessage defaultMessage="Thank you" />
      </Typography>
      <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
        <FormattedMessage defaultMessage="We received your recommendation and will be in touch." />
      </Typography>
    </Stack>
  )
}

function DoulaNotListed({ provider }: { provider: Provider }): JSX.Element {
  const intl = useIntl()
  const { mutate: addMemberAction } = useMemberActionTracking()

  useEffect(() => {
    addMemberAction({
      memberActionTaken: MemberActions.DOULA_RECOMMENDATION_APPROVED_NOT_COMPLETE_FOR_PRODUCT
    })
  }, [addMemberAction])

  const translatedServices = provider.doulaServices.map((service) => intl.formatMessage(doulaServiceLabels[service]))

  return (
    <Box>
      <Typography variant="h2" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
        <FormattedMessage defaultMessage="Approved" />
      </Typography>
      <Typography sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
        <FormattedMessage
          defaultMessage="This doula is eligible to provide {doulaServices} services but chose not to be listed in Provider finder."
          values={{
            doulaServices: <FormattedList value={translatedServices} type="conjunction" />
          }}
        />
      </Typography>
      <Typography>
        <FormattedMessage
          defaultMessage="Questions? <link>Contact us.</link>"
          values={{
            link: (linkContent) => (
              <Link component={RouterLink} to="/talk-to-carrot/sendmessage/helpfindingdoula" color="inherit">
                {linkContent}
              </Link>
            )
          }}
        />
      </Typography>
    </Box>
  )
}

function EligibleDoulaRedirect({ doula }: { doula: TypeaheadDoula }): JSX.Element {
  const providersQuery = useDoulasByIds({
    providerUuids: [doula?.uuid],
    lat: doula?.location?.lat,
    long: doula?.location?.lng
  })

  const eligibleDoula = providersQuery.data?.[0]
  const locationDetailsQuery = useGetLocationDetails(eligibleDoula?.address || "")
  const { mutate: addMemberAction } = useMemberActionTracking()

  useEffect(() => {
    addMemberAction({
      memberActionTaken: MemberActions.DOULA_RECOMMENDATION_APPROVED
    })
  }, [addMemberAction])

  if (providersQuery.isLoading || locationDetailsQuery.isFetching) {
    return <CenteredProgress />
  }

  if (!doula.showInMemberApp) {
    return <DoulaNotListed provider={eligibleDoula} />
  }

  const providerTypeKey =
    Object.keys(PROVIDER_TYPE_SEARCHED).find((key) => PROVIDER_TYPE_SEARCHED[key] === ProviderType.Doulas) || "44"

  const url = getProviderFinderSearchURL({
    searchLocation: eligibleDoula.address,
    locationDetails: locationDetailsQuery.data,
    providerType: providerTypeKey,
    radiusInKm: 50,
    keyword: eligibleDoula.providerName
  })

  return <RedirectToProviderFinderURL url={url} />
}
